<!-- src/views/pages/finance-accounts/sales/invoices-table.vue -->
<script setup>
import { computed } from "vue";
import { useInvoiceStore } from "@/stores/invoiceStore";

const props = defineProps({
  loading: { type: Boolean, default: false },
  meta: {
    type: Object,
    default: () => ({ total: 0, current_page: 1, per_page: 25 }),
  },
});

const emit = defineEmits(["page-change", "delete", "receive-payment"]);

const invoiceStore = useInvoiceStore();
const dataSource = computed(() => invoiceStore.items || []);

const columns = [
  { sorter: false },
  {
    title: "Invoice",
    dataIndex: "invoice_no",
    key: "Invoice",
    sorter: true,
  },
  {
    title: "Name",
    dataIndex: "client",
    key: "Name",
    sorter: true,
  },
  {
    title: "Created On",
    dataIndex: "invoice_date",
    key: "Created_On",
    sorter: true,
  },
  {
    title: "Total",
    dataIndex: "total",
    key: "Total",
    sorter: true,
  },
  {
    title: "Amount Due",
    dataIndex: "amount_due",
    key: "Amount_Due",
    sorter: true,
  },
  {
    title: "Due Date",
    dataIndex: "due_date",
    key: "Due_Date",
    sorter: true,
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "Status",
    sorter: true,
  },
  {
    title: "Compliance",
    dataIndex: "compliance_status",
    key: "Compliance",
    sorter: true,
  },
  {
    title: "",
    key: "action",
    sorter: false,
  },
];

const rowSelection = {
  onChange: () => {},
  onSelect: () => {},
  onSelectAll: () => {},
};

function formatMoney(value) {
  const num = Number(value || 0);
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
}

/**
 * Derived display status using total vs amount_due + raw status
 */
function displayStatus(record) {
  const raw = (record.status || "").toLowerCase();
  const total = Number(record.total || 0);
  const due = Number(record.amount_due || 0);

  if (due <= 0) {
    return "Paid";
  }

  if (due > 0 && total > 0 && due < total) {
    if (raw === "overdue") {
      return "Partially Paid (Overdue)";
    }
    return "Partially Paid";
  }

  if (total > 0 && due === total && raw === "pending") {
    return "Pending";
  }

  if (raw === "draft") return "Draft";
  if (raw === "overdue") return "Overdue";

  if (!raw) return "Draft";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/**
 * Badge classes based on display label
 */
function statusBadgeClasses(label) {
  if (!label) return ["badge", "badge-soft-warning"];
  const s = label.toLowerCase();

  if (s === "paid") return ["badge", "badge-soft-success"];
  if (s === "pending") return ["badge", "badge-soft-purple"];
  if (s === "draft") return ["badge", "badge-soft-warning"];
  if (s.startsWith("partially paid")) return ["badge", "badge-soft-info"];
  if (s.startsWith("overdue")) return ["badge", "badge-soft-danger"];

  return ["badge", "badge-soft-warning"];
}

function complianceLabel(record) {
  const status = (record?.compliance_status || "").toLowerCase();
  if (!status || status === "not_submitted") return "Not Submitted";
  return status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function complianceBadgeClasses(label) {
  const s = (label || "").toLowerCase();

  if (s === "accepted") return ["badge", "badge-soft-success"];
  if (s === "queued") return ["badge", "badge-soft-warning"];
  if (s === "rejected") return ["badge", "badge-soft-danger"];
  if (s === "failed") return ["badge", "badge-soft-danger"];

  return ["badge", "badge-soft-secondary"];
}

/**
 * Should the "Receive Payment" button be hidden?
 * - fully paid (amount_due <= 0) OR
 * - backend already marked as paid
 */
function isFullyPaid(record) {
  const due = Number(record?.amount_due || 0);
  const raw = (record?.status || "").toLowerCase();
  return due <= 0 || raw === "paid";
}

function goToDetails(record) {
  if (!record?.id) return {};
  return { name: "sales.invoices.show", params: { id: record.id } };
}

function goToEdit(record) {
  if (!record?.id) return {};
  return { name: "sales.invoices.edit", params: { id: record.id } };
}

function handleTableChange(pagination) {
  emit("page-change", pagination.current);
}

function handleReceivePayment(record) {
  emit("receive-payment", record);
}
</script>

<template>
  <a-table
    class="table datatable thead-light dq-invoices-table"
    :columns="columns"
    :data-source="dataSource"
    :row-selection="rowSelection"
    :loading="loading"
    :pagination="{
      current: meta.current_page || 1,
      total: meta.total || 0,
      pageSize: meta.per_page || 25,
      showSizeChanger: false,
    }"
    @change="handleTableChange"
    row-key="id"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'Invoice'">
        <div class="d-flex flex-column">
          <router-link :to="goToDetails(record)" class="tb-data" v-can="'Invoice.Read'">
            {{ record.invoice_no }}
          </router-link>
          <span
            v-if="record.adjustment_type"
            class="badge badge-soft-info mt-1 align-self-start"
          >
            {{ record.adjustment_type === "credit_note" ? "Credit Note" : "Debit Note" }}
          </span>
        </div>
      </template>

      <template v-else-if="column.key === 'Name'">
        <div class="d-flex align-items-center">
          <div>
            <h6 class="fw-medium">
              <router-link :to="goToDetails(record)" v-can="'Invoice.Read'">
                {{ record.client?.name || "—" }}
              </router-link>
            </h6>
            <span class="fs-12">{{ record.client?.email || "" }}</span>
          </div>
        </div>
      </template>

      <template v-else-if="column.key === 'Created_On'">
        {{ formatDate(record.invoice_date) }}
      </template>

      <template v-else-if="column.key === 'Total'">
        {{ formatMoney(record.total) }}
      </template>

      <template v-else-if="column.key === 'Amount_Due'">
        {{ formatMoney(record.amount_due) }}
      </template>

      <template v-else-if="column.key === 'Due_Date'">
        {{ formatDate(record.due_date) }}
      </template>

      <template v-else-if="column.key === 'Status'">
        <span
          class="d-inline-flex align-items-center"
          :class="statusBadgeClasses(displayStatus(record))"
        >
          <i class="ti ti-point-filled me-1"></i>
          {{ displayStatus(record) }}
        </span>
      </template>

      <template v-else-if="column.key === 'Compliance'">
        <span
          class="d-inline-flex align-items-center"
          :class="complianceBadgeClasses(complianceLabel(record))"
        >
          <i class="ti ti-point-filled me-1"></i>
          {{ complianceLabel(record) }}
        </span>
      </template>

      <template v-else-if="column.key === 'action'">
        <div class="action-icon d-inline-flex">
          <!-- Hide button when fully paid -->
          <button
            v-if="!isFullyPaid(record)"
            type="button"
            class="btn btn-sm btn-outline-success me-2"
            @click="handleReceivePayment(record)"
            v-can="'Invoice.Edit'"
          >
            Receive Payment
          </button>

          <router-link :to="goToDetails(record)" class="me-2" v-can="'Invoice.Read'">
            <i class="ti ti-eye"></i>
          </router-link>
          <router-link  v-if="!isFullyPaid(record)" :to="goToEdit(record)" class="me-2" v-can="'Invoice.Edit' ">
            <i class="ti ti-edit"></i>
          </router-link>
          <a
           v-if="!isFullyPaid(record)"
            href="javascript:void(0);"
            @click.prevent="emit('delete', record.id)"
            v-can="'Invoice.Delete'"
            
          >
            <i class="ti ti-trash"></i>
          </a>
        </div>
      </template>
    </template>
  </a-table>
</template>

<style scoped>
:deep(.dq-invoices-table .ant-table-cell) {
  vertical-align: top;
}

:deep(.dq-invoices-table .ant-table-tbody > tr > td) {
  white-space: normal;
}

@media (max-width: 767.98px) {
  :deep(.dq-invoices-table .ant-table) {
    min-width: 920px;
  }

  :deep(.dq-invoices-table .tb-data) {
    word-break: break-word;
  }

  :deep(.dq-invoices-table h6) {
    margin-bottom: 0.15rem;
  }
}
</style>
